import { z } from 'zod';
import { evaluateExpression, validateCSVFormat, parseCSVLine } from '../shared';

/**
 * Base schema for individual field validation
 * Keep ALL input values as strings (since inputs emit strings),
 * and let Zod coerce/validate.
 */
export const baseSchema = z.object({
  project: z.string().min(1, 'Project is required'),

  title: z.string().min(1, 'Title is required'),

  length: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null && n >= 0;
  }, 'Length must be a valid expression greater than or equal to 0'),

  tributaryWidth: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null && n >= 0;
  }, 'Tributary Width must be a valid expression greater than or equal to 0'),

  hingeDistance: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null && n >= 0;
  }, 'Hinge Distance must be a valid expression greater than 0 and less than or equal to Length'),

  weight: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null && n >= 0;
  }, 'Weight must be a valid expression greater than or equal to 0'),

  orientation: z.coerce
    .number()
    .int()
    .refine(s => [1, 0].includes(s), 'Orientation must be either 1 (horizontal) or 0 (vertical)'),

  klm: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null && n <= 1 && n >= 0;
  }, 'KLM must be a valid expression between 0 and 1'),

  damping: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null && n >= 0 && n <= 1;
  }, 'Damping must be a valid expression between 0 and 1'),

  resistanceNL: z.string().superRefine((val, ctx) => {
    const err = validateCSVFormat(val);
    if (err) ctx.addIssue({ code: 'custom', message: err });
  }),

  deflectionNL: z.string().superRefine((val, ctx) => {
    const err = validateCSVFormat(val);
    if (err) ctx.addIssue({ code: 'custom', message: err });
  }),

  resistanceLinear: z.string().superRefine((val, ctx) => {
    const err = validateCSVFormat(val);
    if (err) ctx.addIssue({ code: 'custom', message: err });
  }),

  deflectionLinear: z.string().superRefine((val, ctx) => {
    const err = validateCSVFormat(val);
    if (err) ctx.addIssue({ code: 'custom', message: err });
  }),

  pressureHistory: z.string().superRefine((val, ctx) => {
    const err = validateCSVFormat(val);
    if (err) ctx.addIssue({ code: 'custom', message: err });
  }),

  timeHistory: z.string().superRefine((val, ctx) => {
    const err = validateCSVFormat(val);
    if (err) ctx.addIssue({ code: 'custom', message: err });
  }),

  u0: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null && n >= 0;
  }, 'u0 must be a valid expression greater than or equal to 0'),

  v0: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null && n >= 0;
  }, 'v0 must be a valid expression greater than or equal to 0')
});

/**
 * Full schema with cross-field validation
 */
export const schema = baseSchema.superRefine((data, ctx) => {
  const length = evaluateExpression(data.length);
  const hingeDistance = evaluateExpression(data.hingeDistance);
  if (hingeDistance !== null && length !== null && hingeDistance > length) {
    ctx.addIssue({
      code: 'custom',
      message: 'Hinge Distance cannot be greater than Length.',
      path: ['hingeDistance']
    });
  }

  if (parseCSVLine(data.resistanceNL)?.length !== parseCSVLine(data.deflectionNL)?.length) {
    const msg = 'Resistance and deflection Non Linear must have the same number of values.';
    ctx.addIssue({ code: 'custom', message: msg, path: ['resistanceNL'] });
    ctx.addIssue({ code: 'custom', message: msg, path: ['deflectionNL'] });
  }

  if (parseCSVLine(data.resistanceLinear)?.length !== parseCSVLine(data.deflectionLinear)?.length) {
    const msg = 'Resistance and deflection Linear must have the same number of values.';
    ctx.addIssue({ code: 'custom', message: msg, path: ['resistanceLinear'] });
    ctx.addIssue({ code: 'custom', message: msg, path: ['deflectionLinear'] });
  }

  if (parseCSVLine(data.pressureHistory)?.length !== parseCSVLine(data.timeHistory)?.length) {
    const msg = 'Pressure and time histories must have the same number of values.';
    ctx.addIssue({ code: 'custom', message: msg, path: ['pressureHistory'] });
    ctx.addIssue({ code: 'custom', message: msg, path: ['timeHistory'] });
  }
});

/**
 * Helper types
 */
export type FieldName = keyof z.infer<typeof baseSchema>;
export type FormValues = z.input<typeof schema>; // raw form shape (strings from inputs/selects)
export type ParsedValues = z.output<typeof schema>; // parsed shape (numbers after coercion)
export type FormErrors = Partial<Record<FieldName, string | null>>;

/**
 * Validate a single field
 */
export function validateOneField<K extends FieldName>(key: K, raw: unknown): string | null {
  const fieldSchema = baseSchema.shape[key];
  const res = fieldSchema.safeParse(raw);
  return res.success ? null : (res.error.issues[0]?.message ?? 'Invalid value');
}
